import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  FC,
} from 'react';

interface TrayNavigationContextType {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  currentScreenIndex: number;
  navigateForward: () => void;
  navigateBack: () => void;
  setScreenIndex: (index: number) => void;
  bodySlideFrom: 'right' | 'left';
  resetBodySlideFrom: () => void;
  goToScreen: (index: number, animateForward?: boolean) => void;
}

interface TrayNavigationProviderProps {
  children: ReactNode;
}

const TrayNavigationContext = createContext<
  TrayNavigationContextType | undefined
>(undefined);

export const useTrayNavigation = (): TrayNavigationContextType => {
  const context = useContext(TrayNavigationContext);
  if (!context) {
    throw new Error(
      'useTrayNavigation must be used within a TrayNavigationProvider',
    );
  }
  return context;
};

export const TrayNavigationProvider: FC<TrayNavigationProviderProps> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentScreenIndex, setCurrentScreenIndex] = useState(0);
  const [bodySlideFrom, setBodySlideFrom] = useState<'right' | 'left'>('right');

  const navigateForward = (): void => {
    setBodySlideFrom('right');
    setCurrentScreenIndex((prev) => prev + 1);
  };

  const navigateBack = (): void => {
    setBodySlideFrom('left');
    setCurrentScreenIndex((prev) => (prev > 0 ? prev - 1 : 0));
  };

  const resetBodySlideFrom = (): void => {
    setBodySlideFrom('right');
  };

  const goToScreen = (index: number, animateForward: boolean = true): void => {
    setBodySlideFrom(animateForward ? 'right' : 'left');
    setCurrentScreenIndex(index);
  };

  const contextValue: TrayNavigationContextType = {
    isOpen,
    setIsOpen,
    currentScreenIndex,
    navigateForward,
    navigateBack,
    setScreenIndex: setCurrentScreenIndex,
    bodySlideFrom,
    resetBodySlideFrom,
    goToScreen,
  };

  return (
    <TrayNavigationContext.Provider value={contextValue}>
      {children}
    </TrayNavigationContext.Provider>
  );
};
